import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, useConfirmDialog } from '@safetyhub/ui';
import { adminApi } from '../lib/api';
import { apiErrorMessage } from '../lib/apiErrorMessage';
import { useToastStore } from '../stores/toast';
import { OpsManualLink } from '../components/OpsManualLink';
import {
  OPS_CHANNEL_LABELS,
  OPS_INCIDENT_QUERY_KEYS,
  OPS_SEVERITY_LABELS,
  OPS_SEVERITY_ORDER,
  opsIncidentsApi,
  type OpsAlertChannelType,
  type OpsChannelSetting,
  type OpsIncidentSeverity,
} from '../lib/opsIncidents';

/**
 * 내 운영 사건 알림 설정 — 관리자마다 채널을 여럿 고른다(사용자 결정 2026-11-02:
 * 「채널은 지정이(복수 지정 가능) 가능하게 하자. admin마다 다르게 설정하고 싶을 수 있으니」).
 *
 * - admin 화면(운영 사건 목록·머리의 미확인 수)은 항상 보이는 채널이라 여기서 끄고 켜지 않는다.
 * - 채널마다 「최소 심각도」 — 그 이상인 사건만 그 채널로 간다. 같은 사건은 채널마다 하루 1회.
 * - 채널은 이메일·메신저 웹훅 둘이다 — 문자는 운영 알림에 쓰지 않는다(사용자 결정 2026-11-03
 *   「운영 알림은 Slack(웹훅)+이메일, 문자는 아예 제외」).
 * - Sentry 전달은 관리자별이 아니라 플랫폼 전역 설정이다(쓰기는 `system-settings`).
 */
export default function OpsAlertSettings() {
  const queryClient = useQueryClient();
  const showToast = useToastStore((s) => s.showToast);
  const [confirm, confirmDialog] = useConfirmDialog();

  const { data: settings, isLoading } = useQuery({
    queryKey: OPS_INCIDENT_QUERY_KEYS.mySettings,
    queryFn: () => opsIncidentsApi.getMySettings(),
  });
  const { data: permissionsData } = useQuery({
    queryKey: ['admin', 'my-permissions'],
    queryFn: () => adminApi.getMyPermissions(),
    staleTime: 5 * 60 * 1000,
  });
  const canEditGlobal = (permissionsData?.data ?? []).includes('system-settings');
  const { data: globalSettings } = useQuery({
    queryKey: OPS_INCIDENT_QUERY_KEYS.globalSettings,
    queryFn: () => opsIncidentsApi.getGlobalSettings(),
  });

  const [draft, setDraft] = useState<OpsChannelSetting[]>([]);
  useEffect(() => {
    if (settings) setDraft(settings.channels.map((c) => ({ ...c })));
  }, [settings]);

  const save = useMutation({
    mutationFn: () => opsIncidentsApi.updateMySettings(draft),
    onSuccess: (data) => {
      queryClient.setQueryData(OPS_INCIDENT_QUERY_KEYS.mySettings, data);
      showToast('알림 설정을 저장했습니다.', 'success');
    },
    onError: (err) => showToast(apiErrorMessage(err, '알림 설정 저장에 실패했습니다.'), 'error'),
  });

  const test = useMutation({
    mutationFn: (channel: OpsAlertChannelType) => opsIncidentsApi.sendTest(channel),
    onSuccess: (r, channel) =>
      r.success
        ? showToast(`${OPS_CHANNEL_LABELS[channel]} 시험 발송을 보냈습니다.`, 'success')
        : showToast(`${OPS_CHANNEL_LABELS[channel]} 시험 발송 실패: ${r.error ?? '알 수 없는 오류'}`, 'error'),
    onError: (err) => showToast(apiErrorMessage(err, '시험 발송에 실패했습니다.'), 'error'),
  });

  const saveGlobal = useMutation({
    mutationFn: (forwardToSentry: boolean) => opsIncidentsApi.updateGlobalSettings(forwardToSentry),
    onSuccess: (data) => {
      queryClient.setQueryData(OPS_INCIDENT_QUERY_KEYS.globalSettings, data);
      showToast('전역 설정을 저장했습니다.', 'success');
    },
    onError: (err) => showToast(apiErrorMessage(err, '전역 설정 저장에 실패했습니다.'), 'error'),
  });

  const saved = settings?.channels ?? [];
  const isDirty = JSON.stringify(saved.map(pick)) !== JSON.stringify(draft.map(pick));

  const update = (channel: OpsAlertChannelType, patch: Partial<OpsChannelSetting>) =>
    setDraft((prev) => prev.map((c) => (c.channel === channel ? { ...c, ...patch } : c)));

  const handleTest = async (channel: OpsAlertChannelType) => {
    if (isDirty) {
      showToast('저장하지 않은 변경이 있습니다. 시험 발송은 저장된 설정으로 보내므로 먼저 저장하세요.', 'info');
      return;
    }
    const ok = await confirm({
      title: `${OPS_CHANNEL_LABELS[channel]} 시험 발송`,
      body: '저장된 받는 곳으로 실제 메시지를 보냅니다. 보낼까요?',
      confirmLabel: '보내기',
    });
    if (ok) test.mutate(channel);
  };

  if (isLoading || !settings) return <p className="text-sm text-gray-500">불러오는 중…</p>;

  return (
    <div className="space-y-4 max-w-3xl">
      <div>
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-xl font-bold text-gray-900">운영 사건 알림 설정</h2>
          <OpsManualLink topic="alertSettings" />
        </div>
        <p className="text-sm text-gray-500">
          자동화 작업이 실패하면 운영 사건이 만들어지고 관리자 화면 머리에 미확인 수가 보입니다. 여기서 고른 채널로도
          받습니다 — 채널마다 최소 심각도를 정할 수 있고, 같은 사건은 채널마다 하루 한 번만 보냅니다.
        </p>
      </div>

      <Card>
        <div className="space-y-4">
          {draft.map((c) => (
            <div key={c.channel} className="border-b last:border-0 pb-4 last:pb-0">
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-900">
                  <input
                    type="checkbox"
                    checked={c.isEnabled}
                    onChange={(e) => update(c.channel, { isEnabled: e.target.checked })}
                  />
                  {OPS_CHANNEL_LABELS[c.channel]}
                </label>
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-gray-500">최소 심각도</span>
                  <select
                    aria-label={`${OPS_CHANNEL_LABELS[c.channel]} 최소 심각도`}
                    value={c.minSeverity}
                    onChange={(e) => update(c.channel, { minSeverity: e.target.value as OpsIncidentSeverity })}
                    className="border border-gray-300 rounded px-2 py-1"
                  >
                    {OPS_SEVERITY_ORDER.map((s) => (
                      <option key={s} value={s}>
                        {OPS_SEVERITY_LABELS[s]} 이상
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    disabled={test.isPending}
                    onClick={() => handleTest(c.channel)}
                    className="px-2 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                  >
                    시험 발송
                  </button>
                </div>
              </div>
              <div className="mt-2">
                {c.channel === 'EMAIL' && (
                  <div className="flex gap-2 items-center">
                    <input
                      type="email"
                      aria-label="받는 메일 주소"
                      value={c.target ?? ''}
                      onChange={(e) => update(c.channel, { target: e.target.value })}
                      placeholder="ops@example.com"
                      className="flex-1 border border-gray-300 rounded px-2 py-1.5 text-sm"
                    />
                    {settings.loginEmail && c.target !== settings.loginEmail && (
                      <button
                        type="button"
                        onClick={() => update(c.channel, { target: settings.loginEmail })}
                        className="text-xs text-blue-600 hover:underline whitespace-nowrap"
                      >
                        로그인 메일({settings.loginEmail}) 쓰기
                      </button>
                    )}
                  </div>
                )}
                {c.channel === 'WEBHOOK' && (
                  <>
                    <input
                      type="url"
                      aria-label="웹훅 주소"
                      value={c.target ?? ''}
                      onChange={(e) => update(c.channel, { target: e.target.value })}
                      placeholder="https://hooks.slack.com/services/…"
                      className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      https 주소만 받습니다. 본문은 <code>text</code>(Slack·Google Chat 등)와 <code>content</code>(Discord)를
                      함께 싣습니다.
                    </p>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-end mt-4">
          <Button variant="primary" size="sm" disabled={!isDirty || save.isPending} onClick={() => save.mutate()}>
            저장
          </Button>
        </div>
      </Card>

      <Card>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-medium text-gray-900">Sentry로도 보내기 (플랫폼 전역)</div>
            <p className="text-xs text-gray-500">
              새 운영 사건을 Sentry 이슈로도 남깁니다. 관리자별 설정이 아니며, Sentry가 켜진 운영 서버에서만 효과가 있습니다.
              {!canEditGlobal && ' 변경은 시스템 설정 권한이 필요합니다.'}
            </p>
          </div>
          <input
            type="checkbox"
            aria-label="Sentry로도 보내기"
            checked={globalSettings?.forwardToSentry ?? false}
            disabled={!canEditGlobal || saveGlobal.isPending || !globalSettings}
            onChange={(e) => saveGlobal.mutate(e.target.checked)}
          />
        </div>
      </Card>
      {confirmDialog}
    </div>
  );
}

function pick(c: OpsChannelSetting) {
  return { channel: c.channel, isEnabled: c.isEnabled, target: c.target ?? '', minSeverity: c.minSeverity };
}
